import { create } from "zustand";
import AuthService from "../services/auth.service";

const BILLING_EXEMPT_PATHS = ["/select-plan", "/billing/checkout", "/billing", "/logout", "/verify-email"];

export const useAuthStore = create((set, get) => ({
  user: null,
  loading: false,
  // Bootstrap-only flag — true while `initialize()` is validating the session
  // on app load. Route guards (ProtectedRoute/AppRoutes) gate on THIS, not on
  // `loading`: `loading` also flips true/false around login/register/etc, and
  // gating routes on it briefly unmounts the page under it (e.g. it wiped the
  // multi-step institution-registration wizard on a failed submit).
  initializing: false,
  initialized: false,
  error: null,
  // MFA challenge after a correct password (survives the login page re-mounting):
  // { stage: "verify" | "enroll", token, method } | null
  mfaPending: null,

  /** Set to true after institution registration until a plan is activated */
  needsBilling: false,

  /** Selected plan object before checkout — persisted so checkout page can read it */
  selectedPlan: null,

  /** University info returned by registerInstitution */
  university: null,

  initialize: async () => {
    set({ initializing: true });

    const storedUser     = localStorage.getItem("user");
    const storedUniversity = localStorage.getItem("university");
    const storedPlan     = localStorage.getItem("selectedPlan");

    // No cached user profile means the user has never logged in on this device.
    if (!storedUser) {
      set({ user: null, initializing: false, initialized: true });
      return;
    }

    try {
      // Validate the httpOnly cookie session with the server.
      // The access (or refresh) cookie is sent automatically by the browser.
      // If the cookie is expired/missing the 401 interceptor will handle refresh.
      const res = await AuthService.getMe();
      const freshUser = res?.data?.user || res?.data || res?.user;
      // Prefer the server's university (with logo); fall back to the cached one.
      const freshUniversity = res?.data?.university || res?.university || null;

      if (freshUser) {
        localStorage.setItem("user", JSON.stringify(freshUser));
      }
      if (freshUniversity) {
        localStorage.setItem("university", JSON.stringify(freshUniversity));
      }

      // The SERVER is the source of truth for the billing gate. Trusting a
      // sticky localStorage flag here re-trapped admins on /select-plan every
      // refresh, even while on a valid free trial. getMe now returns the live
      // value (no active subscription AND free trial lapsed → true).
      const needsBilling = (res?.data?.requiresBilling ?? res?.requiresBilling) === true;
      localStorage.setItem("needsBilling", String(needsBilling));

      set({
        user: freshUser || JSON.parse(storedUser),
        initializing: false,
        initialized: true,
        needsBilling,
        university: freshUniversity || (storedUniversity ? JSON.parse(storedUniversity) : null),
        selectedPlan: storedPlan ? JSON.parse(storedPlan) : null,
      });
    } catch {
      // Session is invalid — clear local state
      localStorage.removeItem("user");
      localStorage.removeItem("university");
      localStorage.removeItem("needsBilling");
      localStorage.removeItem("selectedPlan");
      set({ user: null, initializing: false, initialized: true });
    }
  },

  /**
   * Apply an authenticated response (password login, MFA verify, or forced-
   * enrolment completion) to the store + persisted state.
   */
  applySession: (response) => {
    if (response.university) {
      localStorage.setItem("university", JSON.stringify(response.university));
    } else {
      localStorage.removeItem("university");
    }
    const stillNeedsBilling = response.requiresBilling === true;
    localStorage.setItem("needsBilling", String(stillNeedsBilling));
    localStorage.removeItem("selectedPlan");
    set({
      user: response.user,
      university: response.university || null,
      loading: false,
      initialized: true,
      needsBilling: stillNeedsBilling,
      selectedPlan: null,
      mfaPending: null,
    });
  },

  clearMfaPending: () => set({ mfaPending: null }),

  login: async (credentials) => {
    set({ loading: true, error: null });
    try {
      const response = await AuthService.login(credentials);

      // MFA gate: the password is correct but a second factor (or forced
      // enrolment) is still required — don't establish a session yet. Store the
      // challenge in the store so it survives the login page re-mounting.
      if (response.mfaRequired || response.mfaSetupRequired) {
        set({
          loading: false,
          mfaPending: {
            stage: response.mfaRequired ? "verify" : "enroll",
            token: response.mfaToken,
            method: response.method || "totp",
          },
        });
        return response;
      }

      get().applySession(response);
      return response;
    } catch (error) {
      set({
        loading: false,
        error: error.response?.data?.message || error.message,
      });
      throw error;
    }
  },

  /** Complete login by verifying the TOTP code / recovery code. */
  verifyMfaLogin: async (body) => {
    set({ loading: true, error: null });
    try {
      const response = await AuthService.loginVerifyMfa(body);
      get().applySession(response);
      return response;
    } catch (error) {
      set({ loading: false, error: error.response?.data?.message || error.message });
      throw error;
    }
  },

  register: async (data) => {
    set({ loading: true, error: null });
    try {
      // Self-registered learners are PENDING admin approval and get NO session
      // (the server sets no cookies). So we do NOT set `user` here — the caller
      // routes them to the "awaiting approval" screen. The response carries
      // { userId, approvalStatus, university, enrollment{ clientSecret? } }.
      const response = await AuthService.register(data);
      set({ loading: false });
      return response;
    } catch (error) {
      set({
        loading: false,
        error: error.response?.data?.message || error.message,
      });
      throw error;
    }
  },

  /** Institution path — creates University + university_admin user */
  registerInstitution: async (data) => {
    set({ loading: true, error: null });
    try {
      const response = await AuthService.registerInstitution(data);
      const needsBilling = response.requiresBilling === true;

      localStorage.setItem("needsBilling", String(needsBilling));
      if (response.university) {
        localStorage.setItem("university", JSON.stringify(response.university));
      }

      set({
        user: response.user,
        university: response.university || null,
        needsBilling,
        loading: false,
        initialized: true,
      });
      return response;
    } catch (error) {
      set({
        loading: false,
        error: error.response?.data?.message || error.message,
      });
      throw error;
    }
  },

  /** Store the plan the user intends to purchase (before checkout) */
  setSelectedPlan: (plan) => {
    localStorage.setItem("selectedPlan", JSON.stringify(plan));
    set({ selectedPlan: plan });
  },

  /** Call after successful payment to clear billing gate */
  clearBillingGate: () => {
    localStorage.removeItem("needsBilling");
    localStorage.removeItem("selectedPlan");
    set({ needsBilling: false, selectedPlan: null });
  },

  logout: async () => {
    try {
      // Server clears the httpOnly auth cookies
      await AuthService.logout();
    } catch {}
    // Clear non-sensitive local state (no tokens were ever in localStorage)
    localStorage.removeItem("user");
    localStorage.removeItem("needsBilling");
    localStorage.removeItem("selectedPlan");
    localStorage.removeItem("university");
    set({ user: null, initialized: true, needsBilling: false, selectedPlan: null, university: null });
  },

  updateUser: (updates) =>
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : state.user,
    })),

  clearError: () => set({ error: null }),

  /** Helper used by ProtectedRoute */
  shouldRedirectToBilling: (pathname) => {
    const { user, needsBilling } = get();
    if (!needsBilling) return false;
    if (user?.role !== "university_admin") return false;
    return !BILLING_EXEMPT_PATHS.some((p) => pathname.startsWith(p));
  },
}));
